import { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Cookies } from "react-cookie";
import { Toast } from "../components/common/toastMessegeComponent";
import TonginLogoIcon from "../components/icon/tonginLogo";

const cookies = new Cookies();

// ─── 브레이크포인트 ───────────────────────────────────────────────────────────
// PC    : ≥1200px  → 좌우 2분할
// 태블릿: 768~1199px → 단일 컬럼, 중앙 카드 (모바일 느낌 + 더 큰 사이즈)
// 모바일: ≤767px   → 단일 컬럼, 풀스크린

// ─── 레이아웃 ────────────────────────────────────────────────────────────────

const Page = styled.div`
  display: flex;
  min-height: 100vh;
  background: #f5f5f7;

  /* 태블릿·모바일: 세로 스택 */
  @media (max-width: 1199px) {
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding-bottom: 48px;
  }

  /* 모바일: 흰 배경으로 인풋과 구분 */
  @media (max-width: 767px) {
    background: #ffffff;
  }
`;

// ─── 브랜드 패널 ──────────────────────────────────────────────────────────────

const BrandPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: clamp(40px, 7vw, 80px) clamp(40px, 6vw, 72px);
  background: linear-gradient(145deg, #ff7f3b 0%, #ff5c00 100%);
  position: relative;
  overflow: hidden;

  /* 장식 원 (PC 전용) */
  &::before {
    content: "";
    position: absolute;
    width: clamp(200px, 40vw, 480px);
    height: clamp(200px, 40vw, 480px);
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.06);
    top: -10%;
    right: -15%;
  }
  &::after {
    content: "";
    position: absolute;
    width: clamp(120px, 24vw, 300px);
    height: clamp(120px, 24vw, 300px);
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.06);
    bottom: 8%;
    left: -8%;
  }

  /* 태블릿: 가로형 배너 */
  @media (min-width: 768px) and (max-width: 1199px) {
    width: 100%;
    flex: none;
    flex-direction: row;
    align-items: center;
    gap: 20px;
    padding: 36px 48px;
    border-radius: 0 0 32px 32px;
    &::before,
    &::after {
      display: none;
    }
  }

  /* 모바일: 더 작은 배너 */
  @media (max-width: 767px) {
    width: 100%;
    flex: none;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    padding: 28px 24px 24px;
    border-radius: 0 0 28px 28px;
    &::before,
    &::after {
      display: none;
    }
  }
`;

const BrandLogo = styled.div`
  width: clamp(52px, 6vw, 72px);
  height: clamp(52px, 6vw, 72px);
  background: rgba(255, 255, 255, 0.18);
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: clamp(28px, 5vw, 48px);
  flex-shrink: 0;
  overflow: hidden;

  /* 태블릿: 배너 안에 */
  @media (min-width: 768px) and (max-width: 1199px) {
    margin-bottom: 0;
    width: 56px;
    height: 56px;
    border-radius: 14px;
  }

  /* 모바일: 배너 안에 */
  @media (max-width: 767px) {
    margin-bottom: 0;
    width: 44px;
    height: 44px;
    border-radius: 12px;
  }
`;

const BrandTextGroup = styled.div`
  position: relative;
  z-index: 1;

  /* 태블릿 */
  @media (min-width: 768px) and (max-width: 1199px) {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  /* 모바일 */
  @media (max-width: 767px) {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const BrandTag = styled.div`
  font-size: clamp(11px, 1.4vw, 14px);
  font-weight: 500;
  color: rgba(255, 255, 255, 0.72);
  letter-spacing: 0.5px;
  margin-bottom: clamp(8px, 1.4vw, 14px);

  @media (max-width: 1199px) {
    display: none;
  }
`;

const BrandName = styled.div`
  font-size: clamp(28px, 3.8vw, 52px);
  font-weight: 800;
  color: #ffffff;
  line-height: 1.15;
  letter-spacing: -0.5px;
  white-space: nowrap;

  /* 태블릿 */
  @media (min-width: 768px) and (max-width: 1199px) {
    font-size: 26px;
    letter-spacing: -0.3px;
  }

  /* 모바일 */
  @media (max-width: 767px) {
    font-size: 20px;
    letter-spacing: -0.3px;
  }
`;

const BrandSub = styled.div`
  margin-top: clamp(10px, 1.6vw, 18px);
  font-size: clamp(13px, 1.5vw, 17px);
  font-weight: 400;
  color: rgba(255, 255, 255, 0.78);
  line-height: 1.6;

  /* 태블릿·모바일에서 배너 옆에 부제목 표시 */
  @media (min-width: 768px) and (max-width: 1199px) {
    margin-top: 0;
    font-size: 15px;
    color: rgba(255, 255, 255, 0.82);
  }

  @media (max-width: 767px) {
    display: none;
  }
`;

// ─── 폼 패널 ─────────────────────────────────────────────────────────────────

const FormPanel = styled.div`
  width: clamp(360px, 44%, 520px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  padding: clamp(40px, 6vw, 72px) clamp(32px, 5vw, 64px);
  background: #ffffff;

  /* 태블릿: 중앙 카드 */
  @media (min-width: 768px) and (max-width: 1199px) {
    width: 100%;
    max-width: 540px;
    margin-top: 40px;
    padding: 44px 48px;
    background: #ffffff;
    border-radius: 24px;
    box-shadow: 0 4px 32px rgba(0, 0, 0, 0.08);
  }

  /* 모바일: 흰 배경, 패딩으로 여백 확보 */
  @media (max-width: 767px) {
    width: 100%;
    padding: 32px 20px 40px;
    background: #ffffff;
  }
`;

const FormTitle = styled.h1`
  font-size: clamp(22px, 2.6vw, 30px);
  font-weight: 700;
  color: #1d1d1f;
  letter-spacing: -0.5px;
  margin-bottom: 6px;

  @media (min-width: 768px) and (max-width: 1199px) {
    font-size: 28px;
    text-align: center;
    margin-bottom: 8px;
  }

  @media (max-width: 767px) {
    font-size: 24px;
  }
`;

const FormSubtitle = styled.p`
  font-size: clamp(13px, 1.4vw, 15px);
  color: #6e6e73;
  font-weight: 400;
  margin-bottom: clamp(28px, 4vw, 40px);

  @media (min-width: 768px) and (max-width: 1199px) {
    font-size: 16px;
    text-align: center;
    margin-bottom: 36px;
  }

  @media (max-width: 767px) {
    font-size: 14px;
  }
`;

// ─── 입력 필드 ────────────────────────────────────────────────────────────────

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: clamp(16px, 2.4vw, 24px);

  @media (min-width: 768px) and (max-width: 1199px) {
    gap: 16px;
    margin-bottom: 28px;
  }
`;

const FieldWrap = styled.div<{ $focused: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: #f5f5f7;
  border-radius: 12px;
  padding: 14px 16px;
  border: 1.5px solid ${(p) => (p.$focused ? "#ff7f3b" : "transparent")};
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
  box-shadow: ${(p) =>
    p.$focused ? "0 0 0 3px rgba(255,127,59,0.12)" : "none"};

  /* 태블릿: 패딩/라운드 확대 */
  @media (min-width: 768px) and (max-width: 1199px) {
    padding: 18px 20px;
    border-radius: 16px;
    gap: 8px;
  }
`;

const FieldLabel = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: #6e6e73;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  user-select: none;

  @media (min-width: 768px) and (max-width: 1199px) {
    font-size: 13px;
  }
`;

const FieldInput = styled.input`
  background: transparent;
  border: none;
  outline: none;
  font-size: clamp(15px, 1.8vw, 17px);
  font-weight: 500;
  color: #1d1d1f;
  font-family: "Pretendard", sans-serif;
  width: 100%;

  &::placeholder {
    color: #b0b0b8;
    font-weight: 400;
  }

  /* 태블릿: 큰 폰트 */
  @media (min-width: 768px) and (max-width: 1199px) {
    font-size: 20px;

    &::placeholder {
      font-size: 18px;
    }
  }
`;

// ─── 아이디 기억하기 ──────────────────────────────────────────────────────────

const RememberRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: clamp(20px, 3vw, 32px);
  cursor: pointer;
  user-select: none;

  @media (min-width: 768px) and (max-width: 1199px) {
    gap: 12px;
    margin-bottom: 32px;
    justify-content: center;
  }
`;

const ToggleBox = styled.div<{ $checked: boolean }>`
  width: 36px;
  height: 20px;
  border-radius: 999px;
  background: ${(p) => (p.$checked ? "#ff7f3b" : "#d1d1d6")};
  position: relative;
  flex-shrink: 0;
  transition: background 0.2s ease;

  &::after {
    content: "";
    position: absolute;
    width: 16px;
    height: 16px;
    background: #ffffff;
    border-radius: 50%;
    top: 2px;
    left: ${(p) => (p.$checked ? "18px" : "2px")};
    transition: left 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.16);
  }

  /* 태블릿: 토글 확대 */
  @media (min-width: 768px) and (max-width: 1199px) {
    width: 48px;
    height: 28px;

    &::after {
      width: 22px;
      height: 22px;
      top: 3px;
      left: ${(p) => (p.$checked ? "23px" : "3px")};
    }
  }
`;

const RememberLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #3a3a3c;

  @media (min-width: 768px) and (max-width: 1199px) {
    font-size: 17px;
  }
`;

// ─── 로그인 버튼 ──────────────────────────────────────────────────────────────

const spinAnim = keyframes`
  to { transform: rotate(360deg); }
`;

const LoginButton = styled.button<{ $active: boolean }>`
  width: 100%;
  height: clamp(50px, 7vw, 58px);
  border-radius: 14px;
  border: none;
  background: ${(p) => (p.$active ? "#ff7f3b" : "#e5e5ea")};
  color: ${(p) => (p.$active ? "#ffffff" : "#aeaeb2")};
  font-size: clamp(15px, 1.8vw, 17px);
  font-weight: 700;
  font-family: "Pretendard", sans-serif;
  letter-spacing: 0.3px;
  cursor: ${(p) => (p.$active ? "pointer" : "not-allowed")};
  transition: background 0.2s ease, transform 0.12s ease, box-shadow 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background: ${(p) => (p.$active ? "#f07030" : "#e5e5ea")};
    box-shadow: ${(p) =>
      p.$active ? "0 4px 16px rgba(255,127,59,0.32)" : "none"};
  }
  &:active {
    transform: ${(p) => (p.$active ? "scale(0.985)" : "none")};
  }

  /* 태블릿: 버튼 크게 */
  @media (min-width: 768px) and (max-width: 1199px) {
    height: 68px;
    font-size: 20px;
    border-radius: 18px;
    letter-spacing: 0.5px;
  }
`;

const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2.5px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spinAnim} 0.7s linear infinite;
`;

const Copyright = styled.div`
  margin-top: clamp(24px, 4vw, 40px);
  font-size: 12px;
  color: #b0b0b8;
  text-align: center;
  font-weight: 400;
`;

// ─── 컴포넌트 ─────────────────────────────────────────────────────────────────

export default function Login() {
  const navigate = useNavigate();
  const rememberAccountString = localStorage.getItem("rememberAccount");
  const rememberAccount =
    rememberAccountString !== null ? JSON.parse(rememberAccountString) : false;

  const [id, setId] = useState("");
  const [pswd, setPswd] = useState("");
  const [loginData, setLoginData] = useState({ id, pswd });
  const [isChecked, setIsChecked] = useState<boolean>(rememberAccount);
  const [idFocused, setIdFocused] = useState(false);
  const [pwFocused, setPwFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchStatus, setFetchStatus] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    if (rememberAccount) {
      const accountString = localStorage.getItem("account");
      const account = accountString && JSON.parse(accountString);
      if (account) {
        setId(account.id);
        setPswd(account.pswd);
      }
    }
  }, []);

  useEffect(() => {
    setLoginData({ id, pswd });
  }, [id, pswd]);

  const isActive = id.length > 1 && pswd.length > 1;

  const onLogin = async () => {
    if (!isActive || isLoading) return;

    setIsLoading(true);
    const requestParam = {
      header: {},
      body: { userId: id, password: pswd },
    };
    try {
      const response: any = await axios.post(
        "https://homenmove.net/v1/api/auth/login",
        requestParam
      );

      localStorage.setItem(
        "accessToken",
        response.data.data.tokens.accessToken
      );

      let isCompanyContractTeam = false;
      let isPlumberTeam = false;
      let isMattressTeam = false;
      let userType = "normal";

      const companyContractBranch = [
        "BE0874", "BE0860", "BE0783", "BE0687",
        "BE0507", "BE0834", "BE0627",
        "BE0216", "BE0811", "BE0885",
      ];
      const mattressTeam = ["BE0995"];
      const plumberTeam = ["BE0996"];

      const userBranchCode = response.data.data.user.branch.branchCode;
      isCompanyContractTeam = companyContractBranch.includes(userBranchCode);
      if (mattressTeam.includes(userBranchCode)) {
        isMattressTeam = true;
        userType = "mattress";
      }
      if (plumberTeam.includes(userBranchCode)) {
        isPlumberTeam = true;
        userType = "plumber";
      }

      const loginUser = {
        ...response.data.data.user,
        isCompanyContractTeam,
        isMattressTeam,
        isPlumberTeam,
        userType,
      };

      localStorage.setItem("loginUser", JSON.stringify(loginUser));
      localStorage.setItem("rememberAccount", JSON.stringify(isChecked));
      localStorage.setItem(
        "account",
        isChecked ? JSON.stringify(loginData) : ""
      );
      cookies.set("refreshToken", response.data.data.tokens.refreshToken);
      navigate("/");
    } catch {
      setIsLoading(false);
      setFetchStatus(true);
      setStatus("FAIL");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") onLogin();
  };

  return (
    <Page>
      {fetchStatus && (
        <Toast
          status={status}
          fetchStatus={fetchStatus}
          setFetchStatus={setFetchStatus}
          text={"아이디 또는 비밀번호를 다시 확인해주세요."}
        />
      )}

      {/* ── 브랜드 패널 ── */}
      <BrandPanel>
        <BrandLogo>
          {/* 흰색 필터로 오렌지 배경 위에 화이트 로고 */}
          <TonginLogoIcon
            width="80%"
            height="80%"
            style={{ filter: "brightness(0) invert(1)" }}
          />
        </BrandLogo>
        <BrandTextGroup>
          <BrandTag>마음까지 통하는 사람들</BrandTag>
          <BrandName>
            통인익스프레스
            <br />
            전자계약시스템
          </BrandName>
          <BrandSub>
            언제 어디서나 빠르고 안전하게.
            <br />
            이사 계약을 디지털로 완성하세요.
          </BrandSub>
        </BrandTextGroup>
      </BrandPanel>

      {/* ── 오른쪽 폼 패널 ── */}
      <FormPanel>
        <FormTitle>로그인</FormTitle>
        <FormSubtitle>계속하려면 계정 정보를 입력해주세요.</FormSubtitle>

        <FieldGroup>
          <FieldWrap $focused={idFocused}>
            <FieldLabel htmlFor="Id">아이디</FieldLabel>
            <FieldInput
              id="Id"
              value={id}
              onChange={(e) => setId(e.target.value)}
              onFocus={() => setIdFocused(true)}
              onBlur={() => setIdFocused(false)}
              placeholder="아이디를 입력해 주세요"
              autoComplete="username"
            />
          </FieldWrap>

          <FieldWrap $focused={pwFocused}>
            <FieldLabel htmlFor="Pw">비밀번호</FieldLabel>
            <FieldInput
              id="Pw"
              type="password"
              value={pswd}
              onChange={(e) => setPswd(e.target.value)}
              onFocus={() => setPwFocused(true)}
              onBlur={() => setPwFocused(false)}
              placeholder="비밀번호를 입력해 주세요"
              onKeyDown={handleKeyDown}
              autoComplete="current-password"
            />
          </FieldWrap>
        </FieldGroup>

        <RememberRow onClick={() => setIsChecked(!isChecked)}>
          <ToggleBox $checked={isChecked} />
          <RememberLabel>아이디 / 비밀번호 기억하기</RememberLabel>
        </RememberRow>

        <LoginButton $active={isActive} onClick={onLogin}>
          {isLoading ? <Spinner /> : "로그인"}
        </LoginButton>

        <Copyright>© 통인익스프레스. All rights reserved.</Copyright>
      </FormPanel>
    </Page>
  );
}
